import { actions, feedback, overlays } from '@ecosystem/newron-design-system';
import React, { useCallback, useEffect, useState } from 'react';
import { usePageConfig } from '@utils/pageConfig/PagesConfigContext';
import { cloneDeep, difference, isEqual } from 'lodash';

import classnames from 'classnames';
import { FilterKeys, Filters, PageName, PageType, SavedFilters, SelectedFilters } from '@utils/pageConfig/Types';
import { Periodicity } from '../time-period-selector/PeriodsTypes';
import { getFilterSettingsByPage, sortFilterTitle, sortOptionsFromApi } from '@utils/FilterSettings';
import { useToastMessage } from '../toast-message/ToastMessage';
import { useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import { actionCreatorsSideFilters } from '@state/Index';
import { getAndStoreSavedFilters } from '@utils/AppSubscriptionUtils';
import {
  isSavedFilterContainBaseFilterWithOtherFilters,
  updateAppliedCurrencyFilterByCountry,
} from '@utils/FilterUtils';
import {
  getCarryOverAllFiltersForApply,
  getExcludedPagesToCarryOver,
  isMultiPgCommonFeatureAllowed,
  removeMultiFeature,
  resetFiltersBasedOnSpecificPageType,
} from '@utils/filterTrayUtils/FilterTrayUtils';
import { byFilterKey } from '@utils/pageConfig/Predicates/SelectedFiltersPredicates';
import {
  ApplyAction,
  canShareFilter,
  DeleteAction,
  FilterCard,
  FilterCardStatus,
  isFilterShared,
  ShareAction,
  ShareFilterModal,
} from '@ecosystem/newron-client-lib';
import { useSharingConfig } from '@services/savedFilter/UseSharingConfig';
import { getEffectiveProductType } from '@models/SubscribedProductType';
import { IRootState } from '@state/reducers/Reducers';
import { UseDeletePersistedFilter } from '@utils/hook/saveFilter/UseDeletePersistedFilter';
import { filterNonSubscribedCountry, getClientSubscriptionMapping } from './ListSavedFiltersSubscription';
import { checkIsFilterApplied } from '@utils/CheckIsFilterApplied';
import { WithAlert } from '@components/design/WithAlert';
import { getExchangeRate, isLocalCurrencyApplied } from '@utils/currencyUtils/CurrencyUtils';
import { getEffectiveProductTypeCatch } from '@components/filters-tray/FiltersTray';
import {
  adjustSelectedFilters,
  processCountryFilterOptions,
  processPricebandFilterOptions,
} from './ListSavedFilterUtils';

interface InfoMessageProps {
  showMissingFiltersInfo: boolean;
}

const InfoMessage = ({ showMissingFiltersInfo }: InfoMessageProps): JSX.Element | null => {
  let infoMessage: string = '';
  const { ActionType, AlertSize } = feedback;
  if (showMissingFiltersInfo) {
    infoMessage = 'Some filter selections won’t be used on this page.';
  }

  if (!infoMessage) {
    return null;
  }
  return <WithAlert title={infoMessage} actionType={ActionType.DEFAULT} size={AlertSize.SMALL} />;
};
/* eslint-disable-next-line */
export interface ListSavedFiltersProps {}

const ListSavedFilters: React.FC<ListSavedFiltersProps> = () => {
  const pagesConfig = usePageConfig();
  const dispatch = useDispatch();
  const { updateSideFilters } = bindActionCreators(actionCreatorsSideFilters, dispatch);

  const subscribedProductTypes = useSelector(
    (state: IRootState) => state.subscribedProductTypes.subscribedProductTypes,
  );

  const savedFilters = filterNonSubscribedCountry(
    [...pagesConfig.savedFilters[pagesConfig.pageType].savedFilters],
    subscribedProductTypes,
    pagesConfig,
  );

  const [isDeleteFiltersModalShown, setIsDeleteFiltersModalShown] = useState<boolean>(false);
  const [filterToDelete, setFilterToDelete] = useState<SavedFilters>({
    id: pagesConfig.userId,
    name: '',
    country: '',
    periodicity: Periodicity.Monthly,
    clientId: -1,
    filters: [],
    permissions: [],
    namespace: '',
    createdAt: new Date().getTime(),
  });

  let filterKeyAllow = getFilterSettingsByPage(pagesConfig.pageType).map((fs) => fs.filterKey);

  const [filterToShare, setFilterToShare] = useState<SavedFilters>({
    id: '1',
    name: '',
    filters: [],
    permissions: [],
    namespace: '',
    createdAt: new Date().getTime(),
    clientId: 1,
    country: '',
    periodicity: Periodicity.Monthly,
  });

  const [saveFilterId, setSaveFilterId] = useState<string>();

  const sharingConfig = useSharingConfig();

  const [isShareModalOpen, setIsShareModalOpen] = useState(false);

  const onCloseShareModal = useCallback(() => {
    setIsShareModalOpen(false);
  }, []);

  useEffect(() => {
    setSaveFilterId('');
  }, [pagesConfig.clearFilters, pagesConfig.selectedFilters]);

  const onShare = async () => {
    const currentProductType = getEffectiveProductType(subscribedProductTypes, pagesConfig.pageName);
    const clientIdMapping = getClientSubscriptionMapping(pagesConfig);

    getAndStoreSavedFilters(currentProductType, clientIdMapping[currentProductType], pagesConfig, pagesConfig.userId);

    setIsShareModalOpen(false);
  };

  const displayDeleteFiltersModal = () => {
    setIsDeleteFiltersModalShown(true);
  };

  const hideDeleteFiltersModal = () => {
    setIsDeleteFiltersModalShown(false);
  };

  const { hideToastMessage, showToastMessage } = useToastMessage();

  useEffect(() => {
    if (isDeleteFiltersModalShown) {
      hideToastMessage();
    }
  }, [isDeleteFiltersModalShown, hideToastMessage]);

  const { Button } = actions;
  const { mutate: deleteFilter } = UseDeletePersistedFilter();

  const onDelete = async () => {
    deleteFilter(filterToDelete.id);

    let allSavedFilters = [...pagesConfig.savedFilters];

    allSavedFilters[pagesConfig.pageType].savedFilters = allSavedFilters[pagesConfig.pageType].savedFilters.filter(
      (e) => e.name !== filterToDelete.name,
    );

    pagesConfig.setSavedFilters(allSavedFilters);
    hideDeleteFiltersModal();

    showToastMessage({
      message: `${filterToDelete.name} filter was deleted`,
      type: 'success',
    });
  };

  const adjustCarryOverFilters = (
    carriedOverAllFilters: SelectedFilters[],
    currentPageFilters: Filters[] | undefined,
    pageWithPG: PageType[],
  ) => {
    return carriedOverAllFilters.map((filter) => adjustSelectedFilters(filter, currentPageFilters, pageWithPG));
  };

  const PRICEBAND_ALLOWED_PAGE_TYPES: PageType[] = [
    PageType.EXPLORE,
    PageType.OPPORTUNITY_REVIEW,
    PageType.TRADING_ASSISTANT,
  ];
  /** Returns true if the given pageType should be excluded from price band notAllowFilterKey logic */
  const isPricebandAllowedPageType = (pageType: PageType): boolean => PRICEBAND_ALLOWED_PAGE_TYPES.includes(pageType);

  const onApply = (card: SavedFilters) => {
    const currentPage = pagesConfig.pageType;
    setSaveFilterId(card.id);
    // Disable clear button by default when apply saved filter
    pagesConfig.setClearFilters(false);

    // The country filter is a base (mandatory) filter that will always be saved when saving a
    // filter. I.e., it will never be null.
    const cardFiltersCountry = cloneDeep([...card.filters].find(byFilterKey(FilterKeys.COUNTRY))!);
    const appliedFiltersCountry = cloneDeep(
      [...pagesConfig.appliedFilters[currentPage].selectedFilters].find(byFilterKey(FilterKeys.COUNTRY))!,
    );

    // configures whether a page has multi-PG, so that multiple PGs could be loaded
    const pageWithPG = [PageType.RI_PRODUCT];

    const appliedFilters = [...cloneDeep(pagesConfig.appliedFilters)];
    const copyOfCard = [...cloneDeep(card.filters)];

    const hasProductGroup = copyOfCard.some(byFilterKey(FilterKeys.PRODUCT_GROUP));
    if (hasProductGroup && pageWithPG.includes(currentPage)) {
      let pgList = copyOfCard.find(byFilterKey(FilterKeys.PRODUCT_GROUP))!;
      pgList.options = pgList.options.length > 1 ? pgList.options.slice(0, 1) : pgList.options;
    }

    if (!hasProductGroup && pageWithPG.includes(currentPage)) {
      let appliedProductGroup = cloneDeep(
        [...pagesConfig.appliedFilters[currentPage].selectedFilters].find(byFilterKey(FilterKeys.PRODUCT_GROUP))!,
      );
      copyOfCard.push(appliedProductGroup);
    }

    const isMultiPg = copyOfCard.some((key) => key.filterKey === FilterKeys.PRODUCT_GROUP && key.options?.length > 1);

    const currencyFilter = copyOfCard.filter(byFilterKey(FilterKeys.CURRENCY));

    const isApplyCurrencyFilter = pagesConfig.isMultiCountryFeatureFlagPA && currencyFilter.length === 0;
    if (isApplyCurrencyFilter) {
      // Handle backward compatibility since currency filter was not saved in the past
      updateAppliedCurrencyFilterByCountry(pagesConfig, cardFiltersCountry.options[0], copyOfCard);
    }

    appliedFilters[currentPage].selectedFilters = cloneDeep(copyOfCard);

    if (cardFiltersCountry.options[0].value !== appliedFiltersCountry.options[0].value) {
      pagesConfig.setIsCountryChanged(true);
    }
    let showPriceBand =
      currencyFilter[0] &&
      !isPricebandAllowedPageType &&
      !isLocalCurrencyApplied(
        pagesConfig,
        getEffectiveProductTypeCatch(subscribedProductTypes, pagesConfig.pageName),
        currencyFilter[0],
        cardFiltersCountry,
      );

    let saveFilterKey = card.filters.map((key) => key.filterKey);
    let notAllowFilterKey = difference(saveFilterKey, filterKeyAllow);
    if (showPriceBand) notAllowFilterKey.push(FilterKeys.PRICEBAND);
    const excludedPagesForCarryOverFilters = getExcludedPagesToCarryOver(pagesConfig);
    const carriedOverAllFilters = getCarryOverAllFiltersForApply(
      appliedFilters,
      currentPage,
      notAllowFilterKey,
      excludedPagesForCarryOverFilters,
    );
    // Retrieve the filters for the current page
    const currentPageFilters = carriedOverAllFilters.find((filter) => filter.pageType === currentPage)?.selectedFilters;

    const adjustedFilters = adjustCarryOverFilters(carriedOverAllFilters, currentPageFilters, pageWithPG);

    removeMultiFeature(adjustedFilters, isMultiPg);

    pagesConfig.setAppliedFilters(adjustedFilters);
    pagesConfig.setSelectedFilters(adjustedFilters);
    const storeFilterSettings = {
      appliedFilters: adjustedFilters,
    };
    resetFiltersBasedOnSpecificPageType(
      pagesConfig,
      updateSideFilters,
      pagesConfig.appliedFilters[currentPage].selectedFilters,
      pagesConfig.selectedFilters[currentPage].selectedFilters,
    );
    updateSideFilters(storeFilterSettings);

    if (
      isSavedFilterContainBaseFilterWithOtherFilters(
        pagesConfig.getPageTypeAndName(),
        copyOfCard,
        pagesConfig.isMultiCountryDropdown,
      )
    ) {
      pagesConfig.setClearFilters(true);
    }
  };

  const filterLabel = (card: SavedFilters) => {
    // logic to check the page filter setting and link it with the saved filter
    // a boolean to see some filter is not in the page filter setting.
    let saveFilterKey = card.filters.map((key) => key.filterKey);
    let notAllowFilterKey = difference(saveFilterKey, filterKeyAllow);
    let currencyFilter = card.filters.filter((key) => key.filterKey === FilterKeys.CURRENCY);
    let countryFilter = card.filters.filter((key) => key.filterKey === FilterKeys.COUNTRY);
    let showPriceBand =
      currencyFilter[0] &&
      !isPricebandAllowedPageType &&
      !isLocalCurrencyApplied(
        pagesConfig,
        getEffectiveProductTypeCatch(subscribedProductTypes, pagesConfig.pageName),
        currencyFilter[0],
        countryFilter[0],
      );
    const isMultiPg = card.filters.some((key) => key.filterKey === FilterKeys.PRODUCT_GROUP && key.options?.length > 1);
    const featureAllowed = isMultiPg && !isMultiPgCommonFeatureAllowed(pagesConfig.pageType);
    return (
      <div data-test-id={'filter-tag'} className="truncate">
        {card.filters.map((filter) => {
          const isExclusionToggleEnabled = filter.isExclusion === true;

          return (
            <div
              className={classnames({
                ['text-inactive']:
                  notAllowFilterKey.includes(filter.filterKey) ||
                  (filter.filterKey === FilterKeys.PRICEBAND && showPriceBand) ||
                  ((filter.filterKey === FilterKeys.ADDFEAT || filter.filterKey === FilterKeys.SEGFEAT) &&
                    featureAllowed),
              })}
              key={`filter-card-${filter.filterKey}`}
            >
              {filter.options.map((option) => {
                return (
                  <div key={`${filter.filterTitle}-${option.value}`}>
                    <span className="text-xs">
                      {isExclusionToggleEnabled ? `${filter.filterTitle} (Excl.): ` : `${filter.filterTitle}: `}
                    </span>
                    <span className="font-bold text-xs" key={option.value}>
                      {option.label}
                    </span>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    );
  };

  const emptyFilters = () => {
    return (
      <div className="p-4">
        <h3 data-testid="no-saved-filters" className="text-base font-bold pb-4">
          No saved filters yet
        </h3>
        <p className="text-sm">Configure a filter then apply it before saving.</p>
      </div>
    );
  };

  const filtersToRender = (currentSavedFilters: SavedFilters[], pageName: PageName): SavedFilters[] => {
    // Multicountry filter changes: Only render cards with single country
    if (
      pagesConfig.isMultiCountryDropdown &&
      pageName !== PageName.PERFORMANCE_ANALYSIS &&
      pageName !== PageName.EXPLORE
    ) {
      filterMultiCountry(currentSavedFilters);
    }
    return currentSavedFilters;
  };

  const filterMultiCountry = (currentSavedFilters: SavedFilters[]) => {
    let array: number[] = [];
    currentSavedFilters.forEach((element, index) => {
      const filters = element.filters.find(byFilterKey(FilterKeys.COUNTRY));
      if (filters && filters.options.length > 1) array.push(index);
    });
    for (let i = array.length - 1; i >= 0; i--) currentSavedFilters.splice(array[i], 1);
    return currentSavedFilters;
  };

  const getPricebandFilters = (savedFilter: SavedFilters, periodicity: string, savedFilterCountry: string[]) => {
    const sideFilterList = pagesConfig.productGroupPriceBandValueList.find(
      (pgPriceband) => savedFilterCountry.includes(pgPriceband.country) && isEqual(pgPriceband.periodType, periodicity),
    )?.productGroupSideMenuFilterDtoList;

    const pg = savedFilter.filters.find((filter) => filter.filterKey === FilterKeys.PRODUCT_GROUP);

    return sideFilterList
      ?.find((features) => features.oldProductGroup.toUpperCase().includes(pg?.options[0].value!))
      ?.sideMenuFilterDtoList.filter((filter) => filter.filterKey === FilterKeys.PRICEBAND)[0];
  };

  const toReturn = (filters: SavedFilters[]) => {
    return (
      <div>
        <div>
          {filtersToRender(filters, pagesConfig.pageName).map((card) => {
            let isFilterApplied = false;
            let isFilterDisabled = false;
            let selectedCountry;
            //Set back option value to label for Priceband Filter so that storeFilterSettings doesn't have the label with Tier
            //since it's used across components
            let countryList = cloneDeep(pagesConfig.clientCountries).concat(pagesConfig.hitlistsClientCountries);
            let savedFilterCountry: string[] = processCountryFilterOptions(card, countryList);

            const featureListAvailable = getPricebandFilters(card, 'MONTHLY', savedFilterCountry);

            const featureListAvailableWeekly = getPricebandFilters(card, 'WEEKLY', savedFilterCountry);
            const currencyExchangeRate = getExchangeRate(pagesConfig, card.filters, selectedCountry);
            const country = card?.filters?.find(byFilterKey(FilterKeys.COUNTRY))!;
            const currency = card?.filters?.find(byFilterKey(FilterKeys.CURRENCY));
            //map and change the display of the card if the priceband index is in the save filter
            processPricebandFilterOptions(card, featureListAvailable, featureListAvailableWeekly, currencyExchangeRate);
            //map back the priceband in the filter

            const copyOfCard = cloneDeep(card);
            const cloneOfCard = cloneDeep(card);
            const appliedFilters: Filters[] = JSON.parse(
              JSON.stringify([...pagesConfig.appliedFilters[pagesConfig.pageType].selectedFilters]),
            );
            const cardFiltersCountry = cloneDeep([...card.filters].find(byFilterKey(FilterKeys.COUNTRY))!);
            const currencyFilter = cloneOfCard.filters.filter(byFilterKey(FilterKeys.CURRENCY));
            const isApplyCurrencyFilter = pagesConfig.isMultiCountryFeatureFlagPA && currencyFilter.length === 0;

            if (isApplyCurrencyFilter) {
              // Handle backward compatibility since currency filter was not saved in the past
              updateAppliedCurrencyFilterByCountry(pagesConfig, cardFiltersCountry.options[0], cloneOfCard.filters);
            }
            cloneOfCard.filters.forEach((e) => (e.options = sortOptionsFromApi(e.options)));
            appliedFilters.forEach((e) => (e.options = sortOptionsFromApi(e.options)));
            const sortedCopyOfCard = sortFilterTitle(cloneOfCard.filters);
            const sortedAppliedFilters = sortFilterTitle(appliedFilters);
            let saveFilterKey = card.filters.map((key) => key.filterKey);

            const saveFilterCardWithAllowedFilterKeyOnly = cloneDeep(card);
            let filterCardWithAllowedFilterKeyOnly = saveFilterCardWithAllowedFilterKeyOnly.filters.filter((f) => {
              return filterKeyAllow.includes(f.filterKey);
            });

            let filterStatus: FilterCardStatus = 'new';
            filterCardWithAllowedFilterKeyOnly = filterCardWithAllowedFilterKeyOnly.filter(
              (f) =>
                !(
                  f.filterKey === FilterKeys.PRICEBAND &&
                  currency &&
                  !isPricebandAllowedPageType &&
                  !isLocalCurrencyApplied(
                    pagesConfig,
                    getEffectiveProductTypeCatch(subscribedProductTypes, pagesConfig.pageName),
                    currency,
                    country,
                  )
                ),
            );
            if (
              checkIsFilterApplied(sortFilterTitle(filterCardWithAllowedFilterKeyOnly), sortedAppliedFilters) ||
              saveFilterId === card.id ||
              checkIsFilterApplied(sortedCopyOfCard, sortedAppliedFilters)
            ) {
              isFilterDisabled = true;
              isFilterApplied = true;
              filterStatus = 'applied';
            }

            const canShare = canShareFilter(card);
            const isShared = isFilterShared(card);

            return (
              <React.Fragment key={card.name}>
                <div className="p-4">
                  <FilterCard filter={card} isShared={isShared} status={filterStatus}>
                    <FilterCard.Content>
                      <div>{filterLabel(card)}</div>
                    </FilterCard.Content>
                    <FilterCard.Footer>
                      <div className="pb-2">
                        <InfoMessage showMissingFiltersInfo={difference(saveFilterKey, filterKeyAllow).length > 0} />
                      </div>
                      <FilterCard.Footer.Actions>
                        <ApplyAction
                          applied={isFilterApplied}
                          disabled={isFilterDisabled}
                          testId="apply-saved-filter"
                          onClick={() => {
                            onApply(copyOfCard);
                          }}
                        />
                        <DeleteAction
                          testId="delete-saved-filter"
                          onClick={() => {
                            setFilterToDelete(card);
                            displayDeleteFiltersModal();
                          }}
                        />
                        {sharingConfig && (
                          <ShareAction
                            testId="share-saved-filter"
                            disabled={!canShare}
                            onClick={() => {
                              setFilterToShare(card);
                              pagesConfig.setSavedFilterToShare(card);
                              setIsShareModalOpen(true);
                            }}
                          />
                        )}
                      </FilterCard.Footer.Actions>
                    </FilterCard.Footer>
                  </FilterCard>
                </div>
              </React.Fragment>
            );
          })}
        </div>
        <div className="mb-4" style={{ minHeight: '30px' }}></div>
      </div>
    );
  };
  const { Modal } = overlays;

  return (
    <>
      <div>{savedFilters.length === 0 ? emptyFilters() : toReturn(savedFilters)}</div>
      {sharingConfig && (
        <ShareFilterModal
          isOpen={!!filterToShare && isShareModalOpen}
          itemToShare={filterToShare}
          context={sharingConfig.market}
          onClose={onCloseShareModal}
          getUsers={sharingConfig.getUsers}
          onSuccess={onShare}
        />
      )}
      <Modal isOpen={isDeleteFiltersModalShown} onClose={hideDeleteFiltersModal}>
        <Modal.Header>Delete filter</Modal.Header>
        <Modal.Body>
          <div data-testid="delete-saved-filter-modal">
            Are you sure you want to delete filter <span>{`'${filterToDelete?.name}'`}</span>?
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button mode="secondary" onClick={hideDeleteFiltersModal}>
            Cancel
          </Button>
          <Button testId={'delete-filter-confirmation'} onClick={onDelete}>
            Delete filter
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ListSavedFilters;
